<template>
  <ModalDialog
    ref="dialog"
    title="Add Adjustment"
    sizeClass="modal-xl"
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    submitTitle="Submit"
    icon="fa-exchange"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!loading && !loadingError">
      <FormRow label="Stock">
        <FormSearchableSelect :options="stockList" v-model="transaction.stockId" placeholder="Stock Code" field="stockId" :dropdownAutoWidth="true" :loading="false" />
      </FormRow>
      <FormRow label="Notes">
        <FormInputText placeholder="Transaction Note" v-model="transaction.note" field="note" />
      </FormRow>
      <FormRow label="Source Account Location" helpTip="Select the source account location for the stock adjustment. Used to filter the inventory bin list for the source bin selection.">
        <AccountLocationSelect v-model="sourceAccountLocationId" placeholder="Source Account Location" field="sourceAccountLocationId" />
      </FormRow>
      <FormRow label="Dest Account Location" helpTip="Select the destination account location for the stock adjustment. Used to filter the inventory bin list for the destination bin selection.">
        <AccountLocationSelect v-model="destAccountLocationId" placeholder="Dest Account Location" field="destAccountLocationId" />
      </FormRow>

      <template v-if="transaction.stockId != undefined">
        <FormHorizontalRule />
        <h3>Incoming / Outgoing List</h3>

        <StaticDataTable class="static-table-list" variant="table-sm">
          <template v-slot:header>
            <tr>
              <th width="5%">Type</th>
              <th width="17%" v-if="stockTypeIsGlass">Glass Size</th>
              <th width="10%" v-if="stockTypeIsGlass">Height</th>
              <th width="10%" v-if="stockTypeIsGlass">Width</th>
              <th width="15%">Source Bin</th>
              <th width="15%">Dest Bin</th>
              <th width="10%">Quantity</th>
              <th width="10%" v-html="uomDisplayUnits"></th>
              <th width="10%">Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(listItem, index) in transaction.transactionList" :key="index">
              <td>
                <span class="label label-stock-transaction-out" v-if="listItem.direction == 'out'">Out</span>
                <span class="label label-stock-transaction-in" v-if="listItem.direction == 'in'">In</span>
                <span class="label label-stock-transaction-moved" v-if="listItem.direction == 'moved'">Moved</span>
              </td>
              <td v-if="stockTypeIsGlass">
                <FormSelect :options="glassSizeListOut" :value="listItem.glassLevelId" @input="glassLevelOutgoingUpdated(listItem, $event)" v-if="isListItemOutgoing(listItem) || isListItemMoved(listItem)" />
                <FormSelect :options="glassSizeListIn" :value="listItem.glassLevelId" @input="glassLevelIncomingUpdated(listItem, $event)" v-if="isListItemIncoming(listItem)" />
              </td>
              <td v-if="stockTypeIsGlass">
                <FormInputText type="number" v-model="listItem.height" v-if="isListItemSizeEditable(listItem)" />
                <span v-if="!isListItemSizeEditable(listItem)">{{listItem.height}}</span>
              </td>
              <td v-if="stockTypeIsGlass">
                <FormInputText type="number" v-model="listItem.width" v-if="isListItemSizeEditable(listItem)" />
                <span v-if="!isListItemSizeEditable(listItem)">{{listItem.width}}</span>
              </td>
              <!-- Source Bin -->
              <td>
                <FormInventoryBinSelect :accountLocationId="sourceAccountLocationId" v-model="listItem.sourceInventoryBinId" placeholder="Inventory Bin" field="sourceInventoryBinId" v-if="canSelectSourceBin(listItem)" />
                <span v-if="!canSelectSourceBin(listItem)">-</span>
              </td>
              <!-- Dest Bin -->
              <td>
                <FormInventoryBinSelect :accountLocationId="destAccountLocationId" v-model="listItem.destInventoryBinId" placeholder="Inventory Bin" field="destInventoryBinId" v-if="canSelectDestBin(listItem)" />
                <span v-if="!canSelectDestBin(listItem)">-</span>
              </td>
              <td>
                <FormInputText type="number" placeholder="Quantity" v-model="listItem.quantity" v-show="!listItem.isOffcut" />
                <span v-if="listItem.isOffcut">1</span>
              </td>
              <td :class="[`stock-transaction-${listItem.direction}`]">
                {{isListItemOutgoing(listItem) ? '-' : ''}}
                {{isListItemIncoming(listItem) ? '+' : ''}}
                {{getListItemAmount(listItem)}}<span v-html="uomDisplayUnits"></span>
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true">
                  <DropdownMenuItem @click="listItem.isSizeOverridden = true" v-if="isListItemSizeOverrideAvailable(listItem)" icon="fa-pencil">Override Size</DropdownMenuItem>
                  <DropdownMenuItem @click="removeRow(index)" icon="fa-trash-alt">Remove</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
            <tr class="table-totals-row-header">
              <td></td>
              <td colspan="3" v-if="stockTypeIsGlass"></td>
              <td colspan="2"></td>
              <td class="text-right font-weight-bold">Net:</td>
              <td :class="{'font-weight-bold': true, 'stock-transaction-in': getNetAdjustAmount() > 0, 'stock-transaction-out': getNetAdjustAmount() < 0}">{{getNetAdjustAmount() > 0 ? '+' : ''}}{{getNetAdjustAmount()}}<span v-html="uomDisplayUnits"></span></td>
              <td></td>
            </tr>
          </template>
        </StaticDataTable>
        <ActionButton type="new" class="btn-xs mr-2" @click="addIncoming"> Add Incoming</ActionButton>
        <ActionButton type="new" class="btn-xs mr-2" @click="addOutgoing"> Add Outgoing</ActionButton>
        <ActionButton type="new" class="btn-xs" @click="addMoved"> Add Moved</ActionButton>
      </template>

    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import Utils from '@/utils/utils'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import StaticDataTable from '@/components/table/staticDataTable.vue'
import FormInventoryBinSelect from '@/modules/inventory/components/formInventoryBinSelect.vue'
import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect.vue'

import { DescriptorLookupTypes, InventoryStockTypes, StockTransactionDirectionTypes, StockTransactionTypes, StockTransactionTypeLookup, UOMTypeUnitsLookup, UOMTypes, InventoryLabelTypes, StockGrainDirectionTypes } from '@/store/types'

export default {
  name: "StockTransactionDialog",
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    StaticDataTable,
    FormInventoryBinSelect,
    AccountLocationSelect
  },
  props: {
  },
  data() {
    return {
      id: null,
      loading: false,
      loadingError: '',
      transaction: {
        stockId: null,
        transactionList: []
      },
      sourceAccountLocationId: null,
      destAccountLocationId: null,
      dialogForm: {},
      submitting: false,
      stockList: [],
      inventoryBinList: [],
      glassLevelList: [],
      glassSizeListIn: [],
      glassSizeListOut: [],
      stockType: null,
      isStockUsingGrain: false,
      uomType: null
    }
  },
  watch: {
    'transaction.stockId': {
      deep: true,
      handler: function(newVal, oldVal) {
        this.$data.glassSizeListIn = []
        this.$data.glassSizeListOut = []
        this.$data.glassLevelList = []

        // Clear transactions if stock changed
        if(newVal != oldVal)
          this.transaction.transactionList = []

        if(this.transaction.stockId == null) {
          return
        }          
        // Update glass size/level list
        this.$apiService.inventoryService.getStock(this.transaction.stockId)
        .then(response => {
          this.stockType = response.data.type
          this.uomType = response.data.uomType
          this.isStockUsingGrain = response.data.isStockUsingGrain

          // Incoming List
          if(_.isArray(response.data.glassSizeList)) {
            this.glassLevelList = response.data.glassSizeList.reduce((list, glassSize) => {
              return _.concat(list, glassSize.glassLevels)
            }, [])
            this.glassLevelList = _.concat(this.glassLevelList, response.data.glassOffcutList)

            // Build size/level list
            this.glassSizeListIn = response.data.glassSizeList.reduce((list, glassSize) => {
              if(!glassSize.isActive || glassSize.glassLevels.length == 0)
                return list
              list.push({
                name: `${glassSize.height}x${glassSize.width}`,
                children: glassSize.glassLevels.filter(glassLevel => !glassLevel.isOffcut).map(glassLevel => {
                  return { value: glassLevel.id, name: `${glassLevel.height}x${glassLevel.width}` }
                })
              })
              return list
            }, [])
          }

          // Incoming - New Offcut
          if(this.isStockUsingGrain) {
            this.glassSizeListIn.push({ name: 'Offcuts', children: [{ value: 'offcut-vertical', name: 'New Offcut - Vertical' }, { value: 'offcut-horizontal', name: 'New Offcut - Horizontal' }] })
          } else {
            this.glassSizeListIn.push({ name: 'Offcuts', children: [{ value: 'offcut', name: 'New Offcut' }] })
          }

          // Outgoing List
          if(_.isArray(response.data.glassSizeList)) {
            this.glassSizeListOut = response.data.glassSizeList.reduce((list, glassSize) => {
              if(!glassSize.isActive || glassSize.glassLevels.length == 0)
                return list
              list.push({
                name: `${glassSize.height}x${glassSize.width}`,
                children: glassSize.glassLevels.filter(glassLevel => !glassLevel.isOffcut).map(glassLevel => {
                  return { value: glassLevel.id, name: `${glassLevel.height}x${glassLevel.width}` }
                })
              })
              return list
            }, [])

            // Outgoing Offcuts
            if(response.data.glassOffcutList.length > 0) {
              this.glassSizeListOut = _.concat(this.glassSizeListOut, {
                name: 'Offcuts',
                children: response.data.glassOffcutList.map(offcut => {
                  return { value: offcut.id, name: `${offcut.height}x${offcut.width} - ${offcut.barcode}` }
                })
              })
            }
          }
        })
      }
    }
  },
  mounted() {
    this.requestData()

    if(this.profileUser && this.profileUser.accountLocationId) {
      this.sourceAccountLocationId = this.profileUser.accountLocationId
      this.destAccountLocationId = this.profileUser.accountLocationId
    }
  },
  computed: {
    ...mapState({
      profileUser: state => state.profile.profileUser
    }),
    stockTypeIsGlass() { return this.stockType == InventoryStockTypes.glass },

    submitDisabled() {
      if(this.transaction.stockId == null)
        return true
      if(this.$data.transaction.transactionList.length == 0)
        return true
      if(this.loading || this.loadingError)
        return true
      return false
    },
    uomDisplayUnits() {
      if(this.$data.uomType == null)
        return ''
      return Utils.getLookupName(UOMTypeUnitsLookup, this.$data.uomType)
    }
  },
  methods: {
    show() {
      // Reset form
      this.transaction = {
        stockId: null,
        transactionList: [],
        note: ''
      }
      this.dialogForm = {}
      this.submitting = false

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.cloneDeep(this.$data.transaction)
      updateData = _.pick(updateData, ['stockId', 'note'])

      updateData.transactionList = this.$data.transaction.transactionList.map(listItem => {
        return {
          direction: listItem.direction,
          glassLevelId: listItem.isNewOffcut ? null : parseInt(listItem.glassLevelId),
          glassSizeId: parseInt(listItem.glassSizeId),
          isOffcut: listItem.isOffcut,
          isOffcutAvailable: listItem.isOffcutAvailable,
          width: listItem.width,
          height: listItem.height,
          quantity: listItem.isOffcut ? 1 : listItem.quantity,
          grainDirection: listItem.grainDirection,
          sourceInventoryBinId: listItem.sourceInventoryBinId,
          destInventoryBinId: listItem.destInventoryBinId
        }
      })

      this.dialogForm = this.clearFormResponse()

      this.$apiService.inventoryService.createStockAdjustment(updateData)
      .then(() => {
        this.submitting = false
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.submitting = false
        this.$data.dialogForm = this.processFormResponse(err)
      })
    },
    requestData() {
      this.loading = true
      this.loadingError = ''
      Promise.all([
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.inventoryService.getInventoryBinList(),
        this.$apiService.coreService.getLabelsForType('inventoryBin')
      ])
      .then(results => {
        this.stockList = results[0].data
        .filter(stock => stock.isActive && (stock.type == InventoryStockTypes.glass || (stock.type == InventoryStockTypes.general && stock.isTrackingQuantity)))
        .map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })

        this.inventoryBinList = results[1].data
        .filter(inventoryBin => inventoryBin.isActive)
        .map(inventoryBin => {
          return {
            value: inventoryBin.id,
            name: inventoryBin.code,
            labelList: inventoryBin.labelList
          }
        })

        this.$store.commit({
          type: 'label/updateLabels',
          labelType: 'inventoryBin',
          labelList: results[2].data
        })
      })
      .catch(err => {
        this.loadingError = 'Error loading inventory data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    addIncoming() {
      this.$data.transaction.transactionList.push({
        direction: StockTransactionDirectionTypes.in,
        glassLevelId: null,
        glassSizeId: null,
        isOffcut: false,
        isNewOffcut: false,
        isSizeOverridden: false,
        width: 0,
        height: 0,
        quantity: 0,
        grainDirection: StockGrainDirectionTypes.none,
        sourceInventoryBinId: this.getDefaultInventoryBinId(),
        destInventoryBinId: this.getDefaultInventoryBinId()
      })
    },
    addOutgoing() {
      this.$data.transaction.transactionList.push({
        direction: StockTransactionDirectionTypes.out,
        glassLevelId: null,
        glassSizeId: null,
        isOffcut: false,
        isNewOffcut: false,
        isSizeOverridden: false,
        width: 0,
        height: 0,
        quantity: 0,
        sourceInventoryBinId: this.getDefaultInventoryBinId(),
        destInventoryBinId: this.getDefaultInventoryBinId()
      })
    },
    addMoved() {
      this.$data.transaction.transactionList.push({
        direction: StockTransactionDirectionTypes.moved,
        glassLevelId: null,
        glassSizeId: null,
        isOffcut: false,
        isNewOffcut: false,
        isSizeOverridden: false,
        width: 0,
        height: 0,
        quantity: 0,
        sourceInventoryBinId: this.getDefaultInventoryBinId(),
        destInventoryBinId: this.getDefaultInventoryBinId()
      })
    },
    removeRow(index) {
      this.$data.transaction.transactionList.splice(index, 1)
    },
    isListItemSizeEditable(item) {
      if(!this.stockTypeIsGlass)
        return false

      if(item.direction == StockTransactionDirectionTypes.in && item.isOffcut)
        return true
      if(item.isSizeOverridden)
        return true

      return false
    },
    isListItemSizeOverrideAvailable(item) {
      if(!this.stockTypeIsGlass)
        return false

      if(this.isListItemSizeEditable(item))
        return false

      if(item.direction == StockTransactionDirectionTypes.in && !item.isOffcut)
        return true

      return false
    },
    isListItemOutgoing(item) {
      return item.direction == StockTransactionDirectionTypes.out
    },
    isListItemIncoming(item) {
      return item.direction == StockTransactionDirectionTypes.in
    },
    isListItemMoved(item) {
      return item.direction == StockTransactionDirectionTypes.moved
    },
    canSelectSourceBin(item) {
      // If an offcut, cannot select source bin. It's set already to the offcut
      if(item.isOffcut)
        return false

      return item.direction != StockTransactionDirectionTypes.in
    },
    canSelectDestBin(item) {
      return item.direction != StockTransactionDirectionTypes.out
    },
    glassLevelOutgoingUpdated(listItem, levelId) {
      listItem.glassLevelId = levelId
      listItem.isOffcut = false
      listItem.isOffcutAvailable = true
      listItem.width = 0
      listItem.height = 0

      var levelItem = this.glassLevelList.find(level => level.id == listItem.glassLevelId)
      if(levelItem != null) {
        listItem.glassSizeId = levelItem.glassSizeId
        listItem.width = levelItem.width
        listItem.height = levelItem.height
        listItem.isOffcut = levelItem.isOffcut
        listItem.quantity = listItem.isOffcut ? 1 : 0
        if(levelItem.isOffcut)
          listItem.sourceInventoryBinId = levelItem.inventoryBinId
      }
    },
    glassLevelIncomingUpdated(listItem, levelId) {
      listItem.glassLevelId = levelId
      listItem.isOffcut = (levelId == 'offcut' || levelId == 'offcut-vertical' || levelId == 'offcut-horizontal')
      listItem.isOffcutAvailable = true
      listItem.isNewOffcut = listItem.isOffcut
      listItem.width = 0
      listItem.height = 0
      listItem.grainDirection = StockGrainDirectionTypes.none
      if(levelId == 'offcut-vertical')
        listItem.grainDirection = StockGrainDirectionTypes.vertical
      else if(levelId == 'offcut-horizontal')
        listItem.grainDirection = StockGrainDirectionTypes.horizontal

      if(listItem.isOffcut)
        listItem.quantity = 1

      if(!listItem.isOffcut) {
        var levelItem = this.glassLevelList.find(level => level.id == listItem.glassLevelId)
        if(levelItem != null) {
          listItem.glassSizeId = levelItem.glassSizeId
          listItem.width = levelItem.width
          listItem.height = levelItem.height
        }
      }      
    },
    getListItemAmount(listItem) {
      if(this.uomType == UOMTypes.metresq)
        return Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(listItem.width*listItem.height*listItem.quantity)).toFixed(3)
      else
        return Utils.roundQuantityValue(listItem.quantity).toFixed(3)
    },
    getNetAdjustAmount() {
      if(this.uomType == UOMTypes.metresq) {
        // Square Metres
        return Utils.roundQuantityValue(this.$data.transaction.transactionList.reduce((curAdjust, listItem) => {
          if(this.isListItemMoved(listItem))
            return curAdjust
          return curAdjust + Utils.roundQuantityValue(Utils.convertMillimetreSqToMetreSq(listItem.width*listItem.height*listItem.quantity)) * (listItem.direction == StockTransactionDirectionTypes.out ? -1 : 1)
        }, 0)).toFixed(3)

      } else {
        // Other quantity
        return Utils.roundQuantityValue(this.$data.transaction.transactionList.reduce((curAdjust, listItem) => {
          if(this.isListItemMoved(listItem))
            return curAdjust

          return curAdjust + Utils.roundQuantityValue(listItem.quantity) * (listItem.direction == StockTransactionDirectionTypes.out ? -1 : 1)
        }, 0)).toFixed(3)
      }
    },
    getDefaultInventoryBinId() {
      var invBinLabels = this.$store.getters['label/getLabelsByType']('inventoryBin')
      var prodSrcLabel = invBinLabels.find(label => label.isFixed && label.statusType == InventoryLabelTypes.defaultProductionStock)

      // Find the first bin with productionSource label
      var defaultBin = this.inventoryBinList.find(invBin => {
        if(prodSrcLabel && invBin.labelList) {
          return invBin.labelList.some(label => label.id == prodSrcLabel.id)
        }
        return false
      })

      // Just use first bin available otherwise
      if(!defaultBin)
        defaultBin = this.inventoryBinList[0]

      if(defaultBin)
        return defaultBin.value
      return null
    }
  }
}
</script>